import i18n from '../../i18n/config';
import { useTranslation } from 'react-i18next';
/**
 * "Cálculo de disparo": explica por qué se abrió el ticket comparando los
 * VALORES AL DISPARO (snapshot inmutable) contra los VALORES ACTUALES.
 *
 * Resuelve la duda operativa de "el panel muestra otros números, ¿de dónde salió
 * la alarma?": el ticket conserva las lecturas con las que se evaluó.
 * Colapsado por defecto para no robar espacio a la información del ticket.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
    Accordion, AccordionSummary, AccordionDetails, Box, Typography, Chip,
    Table, TableBody, TableCell, TableHead, TableRow, Alert, Skeleton, Stack,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FunctionsIcon from '@mui/icons-material/Functions';
import { api } from '../../services/api';

const fmtPsi = (v) => (v === null || v === undefined ? '—' : `${v} PSI`);
const fmtTemp = (v) => (v === null || v === undefined ? '—' : `${v} °C`);

const TicketTriggerCalculation = ({ ticketId }) => {
  const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await api.alarmTickets.ticketTriggerCalculation(ticketId);
            setData(res.data);
        } catch (e) {
            setError(t('hardcoded.components_tickets_tickettriggercalculation.no_se_pudo_cargar_el'));
        } finally {
            setLoading(false);
        }
    }, [ticketId]);

    useEffect(() => {
        if (open && !data && !loading) load();
    }, [open, data, loading, load]);

    const currentFor = (pos) => (data?.current || []).find((c) => c.position === pos) || {};

    return (
        <Accordion
            expanded={open}
            onChange={(_, v) => setOpen(v)}
            sx={{ '&:before': { display: 'none' }, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}
        >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 52 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <FunctionsIcon fontSize="small" color="action" />
                    <Typography variant="subtitle2" fontWeight={700}>{t('hardcoded.components_tickets_tickettriggercalculation.calculo_de_disparo')}</Typography>
                    <Typography variant="caption" color="text.secondary">
                        {t('hardcoded.components_tickets_tickettriggercalculation.valores_al_disparo_vs_valores')}</Typography>
                </Stack>
            </AccordionSummary>

            <AccordionDetails sx={{ pt: 0 }}>
                {loading && <Skeleton variant="rounded" height={180} />}
                {error && <Alert severity="error">{error}</Alert>}

                {data && !loading && (
                    <Box>
                        {data.note && (
                            <Alert severity="info" sx={{ mb: 2 }}>{data.note}</Alert>
                        )}

                        <Table size="small" sx={{ mb: 2 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>{i18n.t('alarms.list.position')}</TableCell>
                                    <TableCell>{t('hardcoded.components_tickets_tickettriggercalculation.valor_al_disparo')}</TableCell>
                                    <TableCell>{t('hardcoded.components_tickets_tickettriggercalculation.antiguedad_de_esa_lectura')}</TableCell>
                                    <TableCell>{i18n.t('hardcoded.components_alarms_alarmlist.valor_actual')}</TableCell>
                                    <TableCell>{i18n.t('hardcoded.components_tickets_tickettriggercalculation.actualizado_hace')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(data.snapshot || []).map((s) => {
                                    const c = currentFor(s.position);
                                    return (
                                        <TableRow key={s.position}>
                                            <TableCell sx={{ fontWeight: 700 }}>{i18n.t('hardcoded.components_tickets_tickettriggercalculation.pos')}{s.position}</TableCell>
                                            <TableCell>{fmtPsi(s.psi)}</TableCell>
                                            <TableCell>{s.age_text || '—'}</TableCell>
                                            <TableCell>
                                                {fmtPsi(c.psi)}
                                                {c.temp !== null && c.temp !== undefined && (
                                                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                                                        ({fmtTemp(c.temp)})
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>{c.age_text || '—'}</TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>

                        {data.formula && (
                            <Box sx={{ mb: 1.5 }}>
                                <Typography variant="overline" color="text.secondary">{t('hardcoded.components_tickets_tickettriggercalculation.formula_aplicada')}</Typography>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{data.formula}</Typography>
                            </Box>
                        )}

                        {(data.steps || []).length > 0 && (
                            <Box component="ul" sx={{ pl: 2.5, m: 0, mb: 1 }}>
                                {data.steps.map((s, i) => (
                                    <Typography component="li" variant="body2" key={i} sx={{ mb: 0.5 }}>{s}</Typography>
                                ))}
                            </Box>
                        )}

                        {data.is_dual && data.limits?.dual_max_reading_age_text && (
                            <Chip
                                size="small"
                                variant="outlined"
                                label={t('hardcoded.components_tickets_tickettriggercalculation.ventana_de_co-temporalidad_dual_max_reading_age_text', { dual_max_reading_age_text: data.limits.dual_max_reading_age_text })}
                            />
                        )}
                    </Box>
                )}
            </AccordionDetails>
        </Accordion>
    );
};

export default TicketTriggerCalculation;
